// The app's motion language — the same curve and cadence the Landing page
// animates on, factored into a module every animated surface reads from.
// EASE_SETTLE is a long expo-out: generic `easeOut` reads as a library default,
// this is the curve that makes an entrance feel authored. The Landing keeps its
// own copy of the constant (it predates this module by a step and is already
// reviewed); the numbers must stay in step.
//
// THE BUDGET. Three durations, and every animation in the app is one of them.
// Adding a fourth means arguing with this comment first — there used to be an
// `exit` duration (220ms) and it was deleted, because the entrance it was
// supposed to beat had moved to `base` (200ms) and 220 > 200 made the module's
// own rule a lie: the exit was slower than the thing it reversed.
//
//   fast  150ms   hover, colour, focus — anything the pointer drives. Fast
//                 enough that the UI feels like it is tracking the cursor
//                 rather than catching up to it. ALSO the exit duration.
//   base  200ms   a state change the user asked for (a tracker step, an
//                 accordion opening, a chevron turning) AND the fade+slide
//                 entrance of a card into a list. Long enough to be seen,
//                 short enough not to be waited on.
//   slow  850ms   an entrance that DRAWS or FILLS: a ring completing, a bar
//                 running out to its value, a gauge sweeping to its score.
//                 These are one continuous gesture rather than a state flip,
//                 so they get the long end of the band — the same 800–900ms
//                 the Landing's own dial draws on.
//
// EXIT IS ALWAYS FASTER THAN ENTER. Entering is the app telling you something;
// leaving is the app getting out of your way. An exit that matches its
// entrance reads as sluggish — you have already moved on. Exits therefore take
// `fast`: they are the one thing this module does that is not a state change
// the user asked for, and 150ms is under the 200ms entrance by construction
// rather than by a fourth number someone has to remember to keep ordering.
//
// ENTRANCES ARE STAGGERED, NEVER SOLO. Sibling cards in a list arrive 60ms
// apart, capped at five steps so a forty-card list cannot park its tail
// invisible for two seconds. Past the cap every card enters together, which
// reads as "the rest of the list arrived" rather than as a queue.
//
// Two rules every helper here follows, both inherited from MASTER.md:
//   • entrances travel on transform + opacity only, so they stay
//     GPU-composited and never reflow the list — never width/height/top/left
//     (the one exception is a progress bar's `scaleX`, which composites and
//     is the only way to draw a fill);
//   • reduced motion collapses every entrance to its final state with no
//     travel, no stagger delay and no draw, so `prefers-reduced-motion` gets
//     the same information without the movement. Everything here is pure
//     config, so tests can pin that contract without DOM animation gymnastics.
//
// `reduced` reaches these functions from app/lib/use-reduced-motion.ts, whose
// server snapshot is a fixed `false`. These values therefore land in the SSR
// markup byte-identically to the client's first paint.

import type { Transition, Variants } from "framer-motion";

/** The settle curve: a long expo-out. */
export const EASE_SETTLE: [number, number, number, number] = [0.16, 1, 0.3, 1];

/**
 * The same curve as a CSS timing function, for the places that animate in CSS
 * rather than through framer (the match gauge's one-pass draw, the donut's
 * arc draw). Kept beside EASE_SETTLE so the two can never drift into
 * different feels.
 */
export const EASE_SETTLE_CSS = "cubic-bezier(0.16, 1, 0.3, 1)";

/** fast — hover, colour, focus. */
export const DUR_FAST = 0.15;
/** base — a state change the user asked for, or a card's fade+slide entrance. */
export const DUR_BASE = 0.2;
/** slow — an entrance that draws or fills: a ring, a bar, a gauge sweep. */
export const DUR_SLOW = 0.85;

/** One step of the sibling-entrance cadence, in seconds. */
export const STAGGER_STEP = 0.06;
/** Past this many items everyone enters together. */
export const STAGGER_CAP = 5;

const REDUCED: Transition = { duration: 0.2, ease: EASE_SETTLE };

/**
 * The travelling-pill spring — the one transition that is choreography rather
 * than an entrance or an exit.
 *
 * A highlight that moves from the old selection to the new one (the Jobs
 * page's filter tints) is the app performing a change rather than reporting
 * one: the eye follows the selection instead of hunting for it. It is
 * deliberately NOT one of the four durations above, because those describe
 * things appearing and disappearing; this describes something travelling, and
 * a duration would make it a fade with extra steps.
 *
 * One pair of numbers for every surface that uses it, so a route change and a
 * filter change read as the same device operating.
 */
export const SPRING_PILL: Transition = { type: "spring", stiffness: 420, damping: 36 };

/**
 * Stagger step for a list, deliberately capped. A five-card list can afford
 * `index * 60ms`; a forty-card list cannot — uncapped, its last card would sit
 * invisible for 2.4s waiting its turn. Past the cap every card enters
 * together.
 */
export function stagger(index: number, step = STAGGER_STEP, cap = STAGGER_CAP): number {
  return Math.min(index, cap) * step;
}

/**
 * A card entering a list: fade in, slide up 10px. Deliberately not a scale or
 * a big travel — a list of peers should settle into place, not fly in. The
 * distance is the same on the dashboard's stat cards and the Jobs cards so the
 * two lists read as one system.
 */
export function enterVariants(reduced: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduced ? 0 : 10 },
    visible: { opacity: 1, y: 0 },
  };
}

export function enterTransition(index = 0, reduced = false): Transition {
  return reduced
    ? { duration: 0.01 }
    : { duration: DUR_BASE, ease: EASE_SETTLE, delay: stagger(index) };
}

/**
 * A job card's entrance, plus the exit shape AnimatePresence plays when a
 * filter removes it. `scale` is tiny on purpose: at 0.985 an exit reads as the
 * card settling back rather than shrinking away, and it keeps the reflow of the
 * cards below it from looking like a jump.
 *
 * The exit carries its OWN transition rather than inheriting the component's.
 * The entrance is staggered by list position (that is what makes a list arrive
 * rather than appear); an exit must not be — a card at index 8 would otherwise
 * linger 350ms after the filter that removed it. Framer resolves a transition
 * declared inside a variant ahead of the `transition` prop, so both behaviours
 * live in one config and callers just name the variant.
 */
export function cardVariants(reduced: boolean): Variants {
  if (reduced) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
      exit: { opacity: 0, transition: exitTransition(true) },
    };
  }
  return {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, scale: 0.985, transition: exitTransition(false) },
  };
}

export function cardTransition(index = 0, reduced = false): Transition {
  return reduced
    ? REDUCED
    : { duration: DUR_BASE, ease: EASE_SETTLE, delay: stagger(index) };
}

/** Exits are quicker than entrances — filtering should feel immediate. */
export function exitTransition(reduced: boolean): Transition {
  return reduced ? { duration: 0.01 } : { duration: DUR_FAST, ease: EASE_SETTLE };
}
